import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input, Modal, message } from 'antd';
import { LoadingOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  CuratorOffersPageStyle,
  CuratorOfferSaveConfirmGlobalStyle,
} from './CuratorOffersPage.style';
import OfferLibrarySidebar from '../../components/CuratorOfferLibrary/OfferLibrarySidebar';
import OfferDetailsFields from '../../components/CuratorOfferLibrary/OfferDetailsFields';
import OfferProfilesPlaylistsStep from '../../components/CuratorOfferModal/OfferProfilesPlaylistsStep';
import { AUDIENCE_COUNTRY_UNKNOWN_VALUE } from '../../constants/isoCountries';
import {
  fetchLinkedCuratorProfiles,
  fetchProfileOwnedPlaylists,
  saveAccountCuratorOffers,
} from '../../services/curatorAccountService';
import {
  buildSharedOfferFields,
  resolveInitialProfilePlaylists,
  type LinkedCuratorProfile,
  type ProfilePlaylistState,
} from '../../utils/accountCuratorOffer';
import {
  assignProfileToOffer,
  buildDefaultOfferLabel,
  buildLiveOfferPlaylistSelections,
  buildProfileOffersPayload,
  cloneOfferTemplate,
  createEmptyOfferTemplate,
  ensureOfferLibrary,
  getUnassignedProfileIds,
  isOfferEditorDirty,
  removeProfileFromOffer,
  saveOfferLibrary,
  type CuratorOfferLibraryState,
  type CuratorOfferTemplate,
  type OfferTerms,
  type ProfilePlaylistSelectionState,
} from '../../utils/curatorOfferLibrary';
import {
  countValidationErrors,
  isOfferDraftComplete,
  validateOfferLabel,
  validateOfferProfiles,
  validateOfferTerms,
  validateOfferTermsField,
} from '../../utils/offerValidation';
import { getAuthAccountUserId } from '../../utils/authAccount';
import {
  isCampaignsOnboardingRequired,
  markCampaignsOnboardingComplete,
} from '../../utils/onboardingIntent';
import type { CuratorOfferSubmissionPlaylist } from '../../utils/curatorOffer';

const CuratorOffersPage: React.FC = () => {
  const accountUserId = getAuthAccountUserId();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkedProfiles, setLinkedProfiles] = useState<LinkedCuratorProfile[]>([]);
  const [library, setLibrary] = useState<CuratorOfferLibraryState | null>(null);
  const [activeOffer, setActiveOffer] = useState<CuratorOfferTemplate | null>(null);
  const [formData, setFormData] = useState<OfferTerms>(buildSharedOfferFields(null));
  const [profilePlaylists, setProfilePlaylists] = useState<Record<string, ProfilePlaylistState>>(
    {}
  );
  const [playlistOptions, setPlaylistOptions] = useState<
    Record<string, CuratorOfferSubmissionPlaylist[]>
  >({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [feedbackTouched, setFeedbackTouched] = useState(false);
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  const loadProfilePlaylists = useCallback(
    async (profile: LinkedCuratorProfile, savedSelection?: ProfilePlaylistSelectionState) => {
      if (!accountUserId) return;

      setProfilePlaylists(prev => ({
        ...prev,
        [profile.id]: {
          options: prev[profile.id]?.options || [],
          selectedIds: savedSelection?.selectedIds || prev[profile.id]?.selectedIds || [],
          loading: true,
          error: null,
        },
      }));

      try {
        const owned = await fetchProfileOwnedPlaylists(profile.id, accountUserId);
        setPlaylistOptions(prev => ({ ...prev, [profile.id]: owned }));

        const savedIds = savedSelection?.selectedIds;
        const { selectedPlaylists } = savedIds?.length
          ? {
              selectedPlaylists: owned.filter(playlist => savedIds.includes(playlist.playlistId)),
            }
          : savedSelection?.includeAllOwnedPlaylists
            ? { selectedPlaylists: owned.map(playlist => ({ ...playlist })) }
            : resolveInitialProfilePlaylists(owned, profile.curatorOffer);

        setProfilePlaylists(prev => ({
          ...prev,
          [profile.id]: {
            options: owned,
            selectedIds: selectedPlaylists.map(playlist => playlist.playlistId),
            loading: false,
            error: null,
          },
        }));
      } catch {
        setProfilePlaylists(prev => ({
          ...prev,
          [profile.id]: {
            options: [],
            selectedIds: [],
            loading: false,
            error: 'Could not load playlists for this profile.',
          },
        }));
      }
    },
    [accountUserId]
  );

  const hydrateActiveOffer = useCallback(
    (offer: CuratorOfferTemplate, profiles: LinkedCuratorProfile[]) => {
      const draft = cloneOfferTemplate(offer);
      setActiveOffer(draft);
      setFormData({ ...draft.terms });
      setErrors({});
      setTouched({});
      setFeedbackTouched(typeof draft.terms.interestedInFeedback === 'boolean');
      setShowValidationSummary(false);
      setProfilePlaylists({});

      draft.profileIds.forEach(profileId => {
        const profile = profiles.find(item => item.id === profileId);
        if (!profile) return;
        loadProfilePlaylists(profile, draft.profilePlaylistSelections[profileId]);
      });
    },
    [loadProfilePlaylists]
  );

  useEffect(() => {
    const initialize = async () => {
      if (!accountUserId) {
        setLoading(false);
        return;
      }

      try {
        const profiles = await fetchLinkedCuratorProfiles(accountUserId);
        setLinkedProfiles(profiles);

        let nextLibrary = ensureOfferLibrary(accountUserId, profiles);
        if (!nextLibrary.offers.length) {
          const emptyOffer = createEmptyOfferTemplate(0);
          nextLibrary = {
            ...nextLibrary,
            offers: [emptyOffer],
            activeOfferId: emptyOffer.id,
          };
          saveOfferLibrary(nextLibrary);
        }

        setLibrary(nextLibrary);
        const selected =
          nextLibrary.offers.find(offer => offer.id === nextLibrary.activeOfferId) ||
          nextLibrary.offers[0];
        if (selected) {
          hydrateActiveOffer(selected, profiles);
        }
      } catch (error) {
        console.error(error);
        message.error('Could not load your offers. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [accountUserId, hydrateActiveOffer]);

  const profileOtherOfferLabels = useMemo(() => {
    if (!library || !activeOffer) return {};
    const labels: Record<string, string> = {};
    library.offers.forEach(offer => {
      if (offer.id === activeOffer.id) return;
      offer.profileIds.forEach(profileId => {
        labels[profileId] = offer.label;
      });
    });
    return labels;
  }, [activeOffer, library]);

  const unassignedCount = library
    ? getUnassignedProfileIds(linkedProfiles, library.offers).length
    : 0;

  const profilePlaylistState = useMemo(
    () =>
      Object.fromEntries(
        (activeOffer?.profileIds || []).map(profileId => [
          profileId,
          {
            loading: profilePlaylists[profileId]?.loading,
            selectedIds: profilePlaylists[profileId]?.selectedIds,
          },
        ])
      ),
    [activeOffer?.profileIds, profilePlaylists]
  );

  const isDraftComplete = activeOffer
    ? isOfferDraftComplete(formData, activeOffer.profileIds, profilePlaylistState)
    : false;

  const savedOffer = library?.offers.find(offer => offer.id === activeOffer?.id);

  const livePlaylistSelections = useMemo(() => {
    if (!activeOffer) return {};
    return buildLiveOfferPlaylistSelections(activeOffer, profilePlaylists, playlistOptions);
  }, [activeOffer, profilePlaylists, playlistOptions]);

  const playlistsReady = useMemo(
    () =>
      (activeOffer?.profileIds || []).every(
        profileId => profilePlaylists[profileId] && !profilePlaylists[profileId].loading
      ),
    [activeOffer?.profileIds, profilePlaylists]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!activeOffer || !playlistsReady) return false;

    return isOfferEditorDirty(savedOffer, {
      label: activeOffer.label,
      terms: formData,
      profileIds: activeOffer.profileIds,
      profilePlaylistSelections: livePlaylistSelections,
    });
  }, [activeOffer, formData, livePlaylistSelections, playlistsReady, savedOffer]);

  const validationErrorMessages = useMemo(() => {
    if (!activeOffer) return [];

    const labelError = validateOfferLabel(activeOffer.label);
    const termsErrors = validateOfferTerms(formData);
    const profileErrors = validateOfferProfiles(activeOffer.profileIds, profilePlaylistState);

    return [labelError, ...Object.values({ ...termsErrors, ...profileErrors })].filter(
      Boolean
    ) as string[];
  }, [activeOffer, formData, profilePlaylistState]);

  const persistLibrary = (nextLibrary: CuratorOfferLibraryState) => {
    saveOfferLibrary(nextLibrary);
    setLibrary(nextLibrary);
  };

  const updateActiveOfferDraft = (patch: Partial<CuratorOfferTemplate>) => {
    setActiveOffer(prev =>
      prev
        ? {
            ...prev,
            ...patch,
            updatedAt: new Date().toISOString(),
          }
        : prev
    );
  };

  const handleSelectOffer = (offerId: string) => {
    if (!library) return;
    const offer = library.offers.find(item => item.id === offerId);
    if (!offer) return;
    const nextLibrary = { ...library, activeOfferId: offerId };
    persistLibrary(nextLibrary);
    hydrateActiveOffer(offer, linkedProfiles);
  };

  const handleCreateOffer = () => {
    if (!library) return;
    const nextOffer = createEmptyOfferTemplate(library.offers.length);
    const nextLibrary = {
      ...library,
      offers: [...library.offers, nextOffer],
      activeOfferId: nextOffer.id,
    };
    persistLibrary(nextLibrary);
    hydrateActiveOffer(nextOffer, linkedProfiles);
  };

  const handleDeleteOffer = () => {
    if (!library || !activeOffer) return;

    Modal.confirm({
      className: 'curator-offer-delete-modal',
      wrapClassName: 'curator-offer-confirm-wrap',
      centered: true,
      title: 'Delete this offer?',
      content:
        'Profiles on this offer will become unassigned. Existing campaigns keep running on their previous terms.',
      okText: 'Delete offer',
      okButtonProps: { danger: true },
      onOk: () => {
        const remaining = library.offers.filter(offer => offer.id !== activeOffer.id);
        const fallbackOffer = createEmptyOfferTemplate(0);
        const nextOffers = remaining.length ? remaining : [fallbackOffer];
        const nextLibrary = {
          ...library,
          offers: nextOffers,
          activeOfferId: nextOffers[0].id,
        };
        persistLibrary(nextLibrary);
        hydrateActiveOffer(
          nextOffers.find(offer => offer.id === nextLibrary.activeOfferId)!,
          linkedProfiles
        );
        message.success('Offer removed from your library.');
      },
    });
  };

  const handleProfileToggle = (profileId: string, checked: boolean) => {
    if (!activeOffer) return;

    if (checked) {
      const profile = linkedProfiles.find(item => item.id === profileId);
      if (profile) {
        loadProfilePlaylists(
          profile,
          activeOffer.profilePlaylistSelections[profileId] || { selectedIds: [] }
        );
      }

      updateActiveOfferDraft({
        profileIds: activeOffer.profileIds.includes(profileId)
          ? activeOffer.profileIds
          : [...activeOffer.profileIds, profileId],
        profilePlaylistSelections: {
          ...activeOffer.profilePlaylistSelections,
          [profileId]: activeOffer.profilePlaylistSelections[profileId] || { selectedIds: [] },
        },
      });
      return;
    }

    const { [profileId]: _removed, ...profilePlaylistSelections } =
      activeOffer.profilePlaylistSelections;

    updateActiveOfferDraft({
      profileIds: activeOffer.profileIds.filter(id => id !== profileId),
      profilePlaylistSelections,
    });
  };

  const handlePlaylistChange = (profileId: string, playlistIds: string[]) => {
    if (!activeOffer) return;

    setProfilePlaylists(prev => ({
      ...prev,
      [profileId]: {
        ...prev[profileId],
        selectedIds: playlistIds,
      },
    }));

    updateActiveOfferDraft({
      profilePlaylistSelections: {
        ...activeOffer.profilePlaylistSelections,
        [profileId]: {
          selectedIds: playlistIds,
          includeAllOwnedPlaylists:
            (playlistOptions[profileId]?.length || 0) > 0 &&
            playlistIds.length >= (playlistOptions[profileId]?.length || 0),
        },
      },
    });
  };

  const handleFieldChange = (field: keyof OfferTerms, value: unknown) => {
    const next = { ...formData, [field]: value } as OfferTerms;
    setFormData(next);
    updateActiveOfferDraft({ terms: next });

    const key =
      field === 'audienceCountryCodes' || field === 'audienceCountriesUnknown'
        ? 'audienceCountries'
        : String(field);
    if (touched[key] || field === 'interestedInFeedback') {
      setErrors(prev => ({
        ...prev,
        [key]: validateOfferTermsField(key, next),
      }));
    }
  };

  const handleLabelChange = (label: string) => {
    updateActiveOfferDraft({ label });
    if (touched.label) {
      setErrors(prev => ({ ...prev, label: validateOfferLabel(label) }));
    }
  };

  const handleCountryChange = (values: string[]) => {
    const hasUnknown = values.includes(AUDIENCE_COUNTRY_UNKNOWN_VALUE);
    const next: OfferTerms = hasUnknown
      ? { ...formData, audienceCountriesUnknown: true, audienceCountryCodes: [] }
      : {
          ...formData,
          audienceCountriesUnknown: false,
          audienceCountryCodes: values.slice(0, 5),
        };
    setFormData(next);
    updateActiveOfferDraft({ terms: next });
    if (touched.audienceCountries) {
      setErrors(prev => ({
        ...prev,
        audienceCountries: validateOfferTermsField('audienceCountries', next),
      }));
    }
  };

  const saveActiveOffer = useCallback(async () => {
    if (!accountUserId || !activeOffer || !library) return;

    setSaving(true);
    try {
      const offerDraft: CuratorOfferTemplate = {
        ...activeOffer,
        terms: formData,
        profilePlaylistSelections: buildLiveOfferPlaylistSelections(
          activeOffer,
          profilePlaylists,
          playlistOptions
        ),
      };

      const profileOffers = buildProfileOffersPayload(offerDraft, playlistOptions);

      const savedOffer = library.offers.find(offer => offer.id === activeOffer.id);
      const removedFromThisOffer = (savedOffer?.profileIds || []).filter(
        profileId => !offerDraft.profileIds.includes(profileId)
      );

      let nextLibrary = library;
      for (const profileId of removedFromThisOffer) {
        nextLibrary = removeProfileFromOffer(nextLibrary, activeOffer.id, profileId);
      }
      for (const profileId of offerDraft.profileIds) {
        nextLibrary = assignProfileToOffer(nextLibrary, activeOffer.id, profileId);
      }

      const autoLabel = buildDefaultOfferLabel(
        formData,
        library.offers.findIndex(o => o.id === activeOffer.id)
      );
      const savedOfferTemplate: CuratorOfferTemplate = {
        ...offerDraft,
        label: offerDraft.label.startsWith('Offer ') ? autoLabel : offerDraft.label,
        updatedAt: new Date().toISOString(),
      };

      nextLibrary = {
        ...nextLibrary,
        activeOfferId: activeOffer.id,
        offers: nextLibrary.offers.map(offer =>
          offer.id === activeOffer.id ? savedOfferTemplate : offer
        ),
      };

      const assignedAfterSave = new Set(nextLibrary.offers.flatMap(offer => offer.profileIds));
      const disabledProfileIds = removedFromThisOffer.filter(
        profileId => !assignedAfterSave.has(profileId)
      );

      let accountEmail: string | undefined;
      let accountName: string | undefined;
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        accountEmail = userInfo?.email?.toString?.().trim?.().toLowerCase?.();
        accountName =
          userInfo?.name?.toString?.().trim?.() ||
          (userInfo?.username?.toString?.().trim?.() && userInfo.username !== userInfo?.email
            ? userInfo.username.toString().trim()
            : undefined);
      } catch {
        // ignore
      }

      await saveAccountCuratorOffers({
        accountUserId,
        sharedOffer: {
          ...formData,
          offerTemplateId: activeOffer.id,
        },
        profileOffers,
        disabledProfileIds,
        email: accountEmail,
        name: accountName,
      });

      persistLibrary(nextLibrary);

      const refreshedProfiles = await fetchLinkedCuratorProfiles(accountUserId);
      setLinkedProfiles(refreshedProfiles);
      hydrateActiveOffer(savedOfferTemplate, refreshedProfiles);

      setShowValidationSummary(false);
      if (isCampaignsOnboardingRequired()) {
        await markCampaignsOnboardingComplete();
      }
      message.success(
        `Offer saved. ${offerDraft.profileIds.length} profile${offerDraft.profileIds.length === 1 ? ' is' : 's are'} now open for receiving campaigns.`
      );
      window.dispatchEvent(new CustomEvent('acceptCampaignsChanged'));
    } catch (error) {
      console.error(error);
      message.error('Failed to save offer. Please try again.');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [
    accountUserId,
    activeOffer,
    formData,
    hydrateActiveOffer,
    library,
    persistLibrary,
    playlistOptions,
    profilePlaylists,
  ]);

  const handleSaveOffer = () => {
    if (!accountUserId || !activeOffer || !library) return;

    const labelError = validateOfferLabel(activeOffer.label);
    const termsErrors = validateOfferTerms(formData);
    const profileErrors = validateOfferProfiles(activeOffer.profileIds, profilePlaylistState);

    const mergedErrors = {
      ...(labelError ? { label: labelError } : {}),
      ...termsErrors,
      ...profileErrors,
    };
    setErrors(mergedErrors);
    setTouched({
      label: true,
      dailyStreamsMin: true,
      dailyStreamsMax: true,
      defaultRatePer1k: true,
      notes: true,
      genres: true,
      interestedInFeedback: true,
      audienceCountries: true,
      profiles: true,
      ...Object.fromEntries(activeOffer.profileIds.map(id => [id, true])),
    });
    setFeedbackTouched(true);
    setShowValidationSummary(true);

    if (countValidationErrors(mergedErrors) > 0) {
      message.error('Fix the highlighted fields before saving.');
      return;
    }

    const profileCount = activeOffer.profileIds.length;

    Modal.confirm({
      className: 'curator-offer-save-modal',
      wrapClassName: 'curator-offer-confirm-wrap',
      centered: true,
      icon: null,
      title: ' ',
      content: (
        <>
          <p className="curator-offer-save-modal__message">
            Save <strong>{activeOffer.label}</strong> for {profileCount} profile
            {profileCount === 1 ? '' : 's'}?
          </p>
          <p className="curator-offer-save-modal__note">
            <span className="curator-offer-save-modal__note-label">Note:</span> Saving will turn on
            receiving campaigns for these profiles. Admins will use this offer when assigning
            campaigns.
          </p>
        </>
      ),
      okText: 'Save offer',
      cancelText: 'Cancel',
      onOk: saveActiveOffer,
    });
  };

  if (loading) {
    return (
      <CuratorOffersPageStyle>
        <div className="curator-offers-loading">
          <LoadingOutlined style={{ fontSize: 28 }} spin />
          <p>Loading your offer library…</p>
        </div>
      </CuratorOffersPageStyle>
    );
  }

  if (!accountUserId) {
    return (
      <CuratorOffersPageStyle>
        <div className="curator-offers-empty">
          <p>Sign in to manage curator offers.</p>
        </div>
      </CuratorOffersPageStyle>
    );
  }

  if (!library || !activeOffer) {
    return (
      <CuratorOffersPageStyle>
        <div className="curator-offers-empty">
          <p>No offers yet. Connect Spotify profiles from account settings first.</p>
        </div>
      </CuratorOffersPageStyle>
    );
  }

  return (
    <CuratorOffersPageStyle>
      <CuratorOfferSaveConfirmGlobalStyle />
      <header className="curator-offers-header">
        <div className="header-inner">
          <h1>My offers</h1>
          <p>
            Build offer packages for your linked Spotify profiles. Each offer can include different
            rates, genres, and playlists, active campaigns always keep their original terms.
          </p>
          <div className="header-links">
            <Link to="/campaigns">← Back to campaigns</Link>
          </div>
        </div>
      </header>

      <div className="curator-offers-body">
        <OfferLibrarySidebar
          offers={library.offers}
          activeOfferId={activeOffer.id}
          unassignedCount={unassignedCount}
          linkedProfileCount={linkedProfiles.length}
          onSelectOffer={handleSelectOffer}
          onCreateOffer={handleCreateOffer}
        />

        <section className="offer-editor">
          <div className="offer-editor__toolbar">
            <div className="offer-editor__label-field">
              <label htmlFor="offer-label">Offer name</label>
              <Input
                id="offer-label"
                className="offer-editor__label-input"
                value={activeOffer.label}
                onChange={event => handleLabelChange(event.target.value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, label: true }));
                  setErrors(prev => ({
                    ...prev,
                    label: validateOfferLabel(activeOffer.label),
                  }));
                }}
                placeholder="e.g. Pop playlists · $8/1K"
                maxLength={80}
                status={touched.label && errors.label ? 'error' : ''}
              />
              {touched.label && errors.label ? (
                <div className="error-message">{errors.label}</div>
              ) : null}
            </div>
            <div className="offer-editor__actions">
              <button
                type="button"
                className="danger-button"
                onClick={handleDeleteOffer}
                disabled={saving || library.offers.length <= 1}
              >
                <DeleteOutlined />
                Delete
              </button>
            </div>
          </div>

          {showValidationSummary && validationErrorMessages.length > 0 ? (
            <div className="offer-editor__validation-banner">
              <strong>Please fix the following before saving:</strong>
              <ul>
                {validationErrorMessages.slice(0, 6).map(msg => (
                  <li key={msg}>{msg}</li>
                ))}
                {validationErrorMessages.length > 6 ? (
                  <li>…and {validationErrorMessages.length - 6} more</li>
                ) : null}
              </ul>
            </div>
          ) : null}

          <div className="offer-editor__steps">
            <div className="offer-editor__step is-active">
              <span className="step-number">1</span>
              Profiles & playlists
            </div>
            <div className="offer-editor__step is-active">
              <span className="step-number">2</span>
              Offer terms
            </div>
          </div>

          <div className="offer-editor__content">
            <p className="offer-editor__note">
              Step 1: Select Spotify profiles and their submission playlists. Step 2: Set rates and
              preferences. Changes apply when you click Save offer; switching offers without saving
              will discard unsaved edits.
            </p>

            <div className="offer-editor__sections">
              <div className="offer-editor__section">
                <h3 className="offer-editor__section-title">
                  <span className="step-number">1</span> Profiles & playlists
                </h3>
                <OfferProfilesPlaylistsStep
                  linkedProfiles={linkedProfiles}
                  selectedProfileIds={activeOffer.profileIds}
                  profilePlaylists={profilePlaylists}
                  profileOtherOfferLabels={profileOtherOfferLabels}
                  errors={errors}
                  touched={touched}
                  disabled={saving}
                  onProfileToggle={handleProfileToggle}
                  onPlaylistChange={handlePlaylistChange}
                  onPlaylistBlur={profileId => setTouched(prev => ({ ...prev, [profileId]: true }))}
                />
              </div>

              <div className="offer-editor__section">
                <h3 className="offer-editor__section-title">
                  <span className="step-number">2</span> Offer terms
                </h3>
                <OfferDetailsFields
                  formData={formData}
                  errors={errors}
                  touched={touched}
                  feedbackTouched={feedbackTouched}
                  disabled={saving}
                  onFieldChange={handleFieldChange}
                  onCountryChange={handleCountryChange}
                  onFeedbackTouched={() => setFeedbackTouched(true)}
                  onBlur={field => setTouched(prev => ({ ...prev, [field]: true }))}
                />
              </div>
            </div>
          </div>

          <div className="offer-editor__footer">
            <span
              className={`offer-editor__footer-status ${isDraftComplete ? 'is-ready' : 'is-incomplete'}`}
            >
              {!hasUnsavedChanges
                ? 'No unsaved changes'
                : isDraftComplete
                  ? `Ready to save · ${activeOffer.profileIds.length} profile${activeOffer.profileIds.length === 1 ? '' : 's'}`
                  : 'Complete all required fields to save this offer'}
            </span>
            <button
              type="button"
              className="primary-button"
              onClick={handleSaveOffer}
              disabled={saving || !hasUnsavedChanges}
            >
              {saving ? 'Saving…' : 'Save offer'}
            </button>
          </div>
        </section>
      </div>
    </CuratorOffersPageStyle>
  );
};

export default CuratorOffersPage;
